import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { MessageSquare, Edit2, Save, X } from 'lucide-react';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787/api';

interface Props {
  clientId: number;
}

export default function ImportantNotesTab({ clientId }: Props) {
  const [client, setClient] = useState<any>(null);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [notes, setNotes] = useState('');

  useEffect(() => {
    loadData();
  }, [clientId]);

  const loadData = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/clients/${clientId}`);
      setClient(response.data);
      setNotes(response.data.important_notes || '');
    } catch (error) {
      console.error('Failed to load client:', error);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await axios.put(`${API_BASE_URL}/clients/${clientId}`, { important_notes: notes });
      await loadData();
      setEditing(false);
    } catch (error) {
      console.error('Failed to save:', error);
      alert('Failed to save notes');
    } finally {
      setSaving(false);
    }
  };

  if (!client) return <div className="text-center py-12 text-gray-500">Loading...</div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <MessageSquare className="w-5 h-5 text-yellow-600" />
          Important Notes
        </h2>
        {!editing ? (
          <button
            onClick={() => setEditing(true)}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 flex items-center gap-2"
          >
            <Edit2 className="w-4 h-4" />
            Edit
          </button>
        ) : (
          <div className="flex gap-2">
            <button
              onClick={() => { setEditing(false); loadData(); }}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              <X className="w-4 h-4 inline mr-1" />
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              <Save className="w-4 h-4 inline mr-1" />
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        )}
      </div>

      <div className="bg-yellow-50 rounded-lg border border-yellow-200 p-6">
        <p className="text-sm text-yellow-800 mb-4">
          <strong>For Account Team:</strong> Critical information about this client's preferences, restrictions, and special requirements.
        </p>
        {editing ? (
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={10}
            className="w-full px-3 py-2 border border-yellow-300 rounded-lg focus:ring-2 focus:ring-yellow-500 bg-white"
            placeholder="Add important notes for the account team..."
          />
        ) : (
          <div className="bg-white rounded-lg p-4 border border-yellow-200">
            {client.important_notes ? (
              <p className="text-gray-700 whitespace-pre-wrap">{client.important_notes}</p>
            ) : (
              <p className="text-gray-500 italic">No important notes added yet</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}